import { useState } from 'react';
import Kader from '../components/Kader';
import { teksten, type Teksten } from '../teksten';
import {
  besteOpnameFormaat,
  bouwTranscript,
  maakHerkenner,
  opnemenBeschikbaar,
} from '../lib/spraak';

/**
 * Microfoontest op /mic.
 *
 * WAAROM DIT BESTAAT. Ik kan hier niet meten of spraakherkenning en opname op
 * een iPhone tegelijk mogen draaien: daar is een echte telefoon en een echte
 * stem voor nodig. Deze pagina laat het toestel het ZELF melden, zodat er
 * gemeten wordt in plaats van aangenomen.
 *
 * Open hem op elk toestel dat je wilt gebruiken.
 */

/**
 * De taal van deze pagina, en waarom hij anders bepaald wordt dan elders.
 *
 * Overal in de app volgt de taal de keuze die je bij het binnenkomen maakte.
 * Deze pagina staat BUITEN een kamer: er is geen keuze om te volgen. Daarom
 * geeft de link op het voorportaal de gekozen taal mee als ?taal=xx, en valt
 * de kale /mic terug op de taal van de browser. Kent teksten() die niet, dan
 * wordt het Engels - dezelfde terugval als overal.
 */
function leesTaal(): string {
  const uitDeLink = new URLSearchParams(window.location.search).get('taal');
  if (uitDeLink !== null && uitDeLink !== '') return uitDeLink;
  return navigator.language;
}

/**
 * De foutcode van de browser, in woorden.
 *
 * De CODE zelf blijft ernaast staan in het scherm: het verschil tussen
 * 'network' en 'not-allowed' bepaalt of het aan de verbinding ligt of aan
 * toestemming, en dat is precies waarvoor je deze pagina opent.
 */
function foutInWoorden(code: string, t: Teksten): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return t.foutMicroGeweigerd;
    case 'no-speech':
      return t.foutNietsGehoord;
    case 'audio-capture':
      return t.foutGeenMicro;
    case 'network':
      return t.micNetwerkFout;
    case 'aborted':
      return t.micAfgebroken;
    default:
      return t.foutMicroOnbekend;
  }
}

type Stand = 'nog niet' | 'bezig' | 'gelukt' | 'mislukt';

interface Uitslag {
  stand: Stand;
  detail: string;
}

const START: Uitslag = { stand: 'nog niet', detail: '' };

export default function MicTestPage() {
  const [taalcode] = useState(leesTaal);
  const t = teksten(taalcode);

  const [herkenning, setHerkenning] = useState<Uitslag>(START);
  const [opname, setOpname] = useState<Uitslag>(START);
  const [samen, setSamen] = useState<Uitslag>(START);
  const [omgekeerd, setOmgekeerd] = useState<Uitslag>(START);
  const [gehoord, setGehoord] = useState('');
  const [fase, setFase] = useState('');

  /** Fase 1: alleen herkenning. Geen opname erbij. */
  async function testHerkenning(): Promise<boolean> {
    const herkenner = maakHerkenner();
    if (herkenner === null) {
      setHerkenning({ stand: 'mislukt', detail: t.micGeenHerkenning });
      return false;
    }

    return new Promise<boolean>((klaar) => {
      let gehoordIets = false;
      // De taal van de LEZER is ook de taal die hij gaat inspreken. Vroeger
      // stond hier 'nl-NL' vast, en dan meet een Hongaar zijn microfoon met
      // een Nederlands model - dat mislukt om de verkeerde reden.
      herkenner.lang = taalcode;
      herkenner.interimResults = true;
      herkenner.continuous = true;
      herkenner.maxAlternatives = 1;

      herkenner.onresult = (e) => {
        gehoordIets = true;
        // Via bouwTranscript, net als de app zelf. Alles zomaar aan elkaar
        // plakken geeft "halloHalloHallo dit isHallo dit is een" - dat waren
        // de tussenresultaten, elke keer opnieuw meegeteld.
        const { vast, lopend } = bouwTranscript(e.results);
        setGehoord([vast, lopend].filter((d) => d !== '').join(' '));
        setHerkenning({ stand: 'gelukt', detail: t.micWoordenBinnen });
      };
      herkenner.onerror = (e) => {
        setHerkenning({ stand: 'mislukt', detail: `${e.error} — ${foutInWoorden(e.error, t)}` });
      };

      try {
        herkenner.start();
        setHerkenning({ stand: 'bezig', detail: t.micZegIets });
      } catch (fout) {
        setHerkenning({
          stand: 'mislukt',
          detail: fout instanceof Error ? fout.message : t.micKonNietStarten,
        });
        klaar(false);
        return;
      }

      window.setTimeout(() => {
        herkenner.stop();
        window.setTimeout(() => klaar(gehoordIets), 300);
      }, 5000);
    });
  }

  /** Fase 2: alleen opnemen. Geen herkenning erbij. */
  async function testOpname(): Promise<boolean> {
    if (!opnemenBeschikbaar()) {
      setOpname({ stand: 'mislukt', detail: t.micGeenRecorder });
      return false;
    }
    try {
      const stroom = await navigator.mediaDevices.getUserMedia({ audio: true });
      const formaat = besteOpnameFormaat();
      const stukken: Blob[] = [];
      const recorder = new MediaRecorder(
        stroom,
        formaat !== null ? { mimeType: formaat } : undefined,
      );
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) stukken.push(e.data);
      };
      recorder.start();
      setOpname({ stand: 'bezig', detail: t.micOpnemen });

      await new Promise((r) => setTimeout(r, 3000));
      recorder.stop();
      await new Promise((r) => setTimeout(r, 400));
      stroom.getTracks().forEach((tr) => tr.stop());

      const bytes = stukken.reduce((n, b) => n + b.size, 0);
      const ok = bytes > 0;
      setOpname({
        stand: ok ? 'gelukt' : 'mislukt',
        detail: ok
          ? `${Math.round(bytes / 1024)} kB, ${formaat ?? t.micGeen}`
          : t.micNietsOpgenomen,
      });
      return ok;
    } catch (fout) {
      setOpname({
        stand: 'mislukt',
        detail: fout instanceof Error ? `${fout.name}: ${fout.message}` : t.micGeenMicrofoon,
      });
      return false;
    }
  }

  /**
   * Allebei tegelijk. DE VOLGORDE IS DE VRAAG: wie de microfoon als eerste
   * pakt, kan de ander buitensluiten. Daarom twee keer, andersom.
   */
  async function testSamen(opnameEerst: boolean, zet: (u: Uitslag) => void): Promise<void> {
    zet({ stand: 'bezig', detail: opnameEerst ? t.micOpnameEerst : t.micHerkenningEerst });

    let stroom: MediaStream | null = null;
    const stukken: Blob[] = [];
    let recorder: MediaRecorder | null = null;
    let herkendIets = false;
    let herkenFout: string | null = null;

    const startOpname = async (): Promise<boolean> => {
      try {
        stroom = await navigator.mediaDevices.getUserMedia({ audio: true });
        const formaat = besteOpnameFormaat();
        recorder = new MediaRecorder(stroom, formaat !== null ? { mimeType: formaat } : undefined);
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) stukken.push(e.data);
        };
        recorder.start();
        return true;
      } catch (fout) {
        zet({
          stand: 'mislukt',
          detail: `${t.micOpnameStarttNiet}: ${fout instanceof Error ? fout.name : '?'}`,
        });
        return false;
      }
    };

    const herkenner = maakHerkenner();
    const startHerkenning = (): void => {
      if (herkenner === null) return;
      herkenner.lang = taalcode;
      herkenner.interimResults = true;
      herkenner.continuous = true;
      herkenner.onresult = () => {
        herkendIets = true;
      };
      herkenner.onerror = (e) => {
        herkenFout = e.error;
      };
      try {
        herkenner.start();
      } catch {
        herkenFout = t.micKonNietStarten;
      }
    };

    if (opnameEerst) {
      if (!(await startOpname())) return;
      // Even wachten: de microfoon moet echt bezet zijn voordat de ander hem
      // probeert te pakken, anders meet je een race in plaats van een regel.
      await new Promise((r) => setTimeout(r, 400));
      startHerkenning();
    } else {
      startHerkenning();
      await new Promise((r) => setTimeout(r, 400));
      if (!(await startOpname())) return;
    }

    await new Promise((r) => setTimeout(r, 5000));
    herkenner?.stop();
    (recorder as MediaRecorder | null)?.stop();
    await new Promise((r) => setTimeout(r, 400));
    (stroom as MediaStream | null)?.getTracks().forEach((tr) => tr.stop());

    const bytes = stukken.reduce((n, b) => n + b.size, 0);
    if (herkendIets && bytes > 0) {
      zet({
        stand: 'gelukt',
        detail: t.micSamenGelukt.replace('{kb}', String(Math.round(bytes / 1024))),
      });
    } else if (bytes > 0) {
      zet({
        stand: 'mislukt',
        detail: `${t.micAlleenOpname}${herkenFout !== null ? ` (${herkenFout})` : ''}`,
      });
    } else if (herkendIets) {
      zet({ stand: 'mislukt', detail: t.micAlleenHerkenning });
    } else {
      zet({ stand: 'mislukt', detail: t.micGeenVanBeide });
    }
  }

  async function testen(): Promise<void> {
    setGehoord('');
    setHerkenning(START);
    setOpname(START);
    setSamen(START);
    setOmgekeerd(START);

    // VIER APARTE FASEN, na elkaar. Alleen zo is te zien of de herkenning
    // sowieso niet werkt, of alleen niet TERWIJL er opgenomen wordt.
    setFase(t.micFase1);
    await testHerkenning();

    setFase(t.micFase2);
    await testOpname();

    setFase(t.micFase3);
    await testSamen(true, setSamen);

    setFase(t.micFase4);
    await testSamen(false, setOmgekeerd);

    setFase(t.micKlaar);
  }

  return (
    <Kader titel={t.micTitel} onderschrift={t.micOnderschrift} taalcode={taalcode}>
      <button
        type="button"
        onClick={() => void testen()}
        className="w-full py-3 mb-4 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                   font-bold text-sm transition-colors duration-155"
      >
        {t.micStart}
      </button>

      {fase !== '' && (
        <p className="mb-3 px-3 py-2 bg-gio-blue/8 border-2 border-gio-blue/25 rounded-xl
                      text-xs font-bold text-gio-dark">
          {fase}
        </p>
      )}

      <div className="space-y-2">
        <Regel naam={t.micTest1} uitslag={herkenning} />
        <Regel naam={t.micTest2} uitslag={opname} />
        <Regel naam={t.micTest3} uitslag={samen} />
        <Regel naam={t.micTest4} uitslag={omgekeerd} />
      </div>

      {gehoord !== '' && (
        <p className="mt-3 px-3 py-2 bg-gio-blue/8 border-2 border-gio-blue/25 rounded-xl
                      text-xs italic text-slate-650">
          {t.micGehoord}: {gehoord}
        </p>
      )}

      <dl className="mt-5 pt-4 border-t-2 border-slate-150 text-[11px] text-slate-450 space-y-1">
        <div className="flex justify-between gap-3">
          <dt>{t.micOpnameformaat}</dt>
          <dd className="font-mono text-right">{besteOpnameFormaat() ?? t.micGeen}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>{t.micBeveiligd}</dt>
          <dd className="font-mono">{window.isSecureContext ? t.micJa : t.micNee}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>{t.micBrowser}</dt>
          <dd className="font-mono text-right break-all">{navigator.userAgent.slice(0, 60)}</dd>
        </div>
      </dl>

      {!window.isSecureContext && <p className="mt-3 text-xs text-gio-danger">{t.micGeenHttps}</p>}
    </Kader>
  );
}

function Regel({ naam, uitslag }: { naam: string; uitslag: Uitslag }) {
  const stijl = {
    'nog niet': 'border-slate-250 bg-slate-50 text-slate-450',
    bezig: 'border-gio-blue/30 bg-gio-blue/5 text-gio-blue',
    gelukt: 'border-emerald-250 bg-emerald-50 text-emerald-650',
    mislukt: 'border-gio-danger/30 bg-gio-danger/5 text-gio-danger',
  }[uitslag.stand];

  const teken = { 'nog niet': '–', bezig: '…', gelukt: '✓', mislukt: '✗' }[uitslag.stand];

  return (
    <div className={`border-2 rounded-xl px-3 py-2.5 ${stijl}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold">{naam}</span>
        <span className="font-mono text-sm">{teken}</span>
      </div>
      {uitslag.detail !== '' && (
        <p className="text-[11px] mt-0.5 opacity-80 break-words">{uitslag.detail}</p>
      )}
    </div>
  );
}
